import { invalidateConfigQueries } from "@/lib/config-pagination";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { PanelRightOpen, Pencil, Plus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type {
  ChangedItem,
  DraftOperation,
  OrgUnitQuery,
  OrgUnitRow,
} from "@/api/management";
import { CorrectedValue } from "@/components/agents/configuration-server/CorrectedValue";
import type { OrgSelection } from "@/components/agents/configuration-server/OrgStructureListView";
import { BoundedOrgTool } from "./BoundedOrgTool";
import { BoundedOrgMap } from "./BoundedOrgMap";
import { canEditOrgUnit } from "@/components/agents/configuration-server/orgUnitState";
import { OrgUnitStatusBadge } from "@/components/agents/configuration-server/OrgUnitStatusBadge";
import {
  ScopeVerdictDialog,
  type ScopeUnit,
} from "@/components/agents/configuration-server/ScopeVerdictDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BulkActions,
  SelectAllBar,
} from "@/components/agents/configuration-server/stages/BulkActions";
import {
  ApproveButton,
  ChildrenError,
  ChildrenLoading,
  DecidedPanelBody,
  DecidedRow,
  DecisionRow,
  DiagnosticBadge,
  EmptyPending,
  FILTER_SELECT_CLASS,
  GhostIconButton,
  MissingName,
  OutOfScopeButton,
  PendingActions,
  SourceSegment,
  type SourceFilter,
} from "@/components/agents/configuration-server/stages/DecisionRows";
import type { NodeCountsView } from "@/components/agents/configuration-server/stages/ActivityManagementTab";
import { bulkVerdictError } from "@/api/config-server-stages";
import { errorDetailOr } from "@/lib/api-error";
import { configurationErrorMessage } from "@/lib/config-server-messages";
import {
  useDraftVerdicts,
  useDraftItems,
  useManagementDraft,
} from "@/components/agents/configuration-server/stages/managementDraftContext";
import { ManagementToolbar } from "@/components/agents/configuration-server/stages/ManagementToolbar";
import { DraftDetailsMore } from "./ActivityManagementTab";
import { PANEL_PAGE_SIZE } from "@/lib/config-pagination";
import { LoadMore } from "@/components/agents/configuration-server/stages/Paging";
import { TransferLayout } from "@/components/agents/configuration-server/stages/TransferLayout";
import { StageLoading } from "@/components/agents/configuration-server/stages/StageLoading";
import { Button } from "@/components/ui/button";
import { stageKeys } from "@/hooks/useConfigServerStages";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useLiveAnnouncement } from "@/hooks/useLiveAnnouncement";
import {
  useOrgFacets,
  useOrgUnitsPage,
  useOrgUnitsScroll,
} from "@/hooks/useManagementLists";
import {
  orgUnitTypeLabelKey,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";
import { cn } from "@/lib/utils";

type Verdict = Exclude<ScopeVerdict, "pending">;
type ToolRequest =
  | { kind: "details" | "edit"; key: string }
  | { kind: "create" };

interface Picked {
  key: string;
  row?: OrgUnitRow;
  withDescendants: boolean;
}
/** "Todos" é uma instrução ao servidor; desmarcar um vira a lista do que está na tela. */
type Selection =
  | { mode: "all" }
  | { mode: "some"; items: ReadonlyMap<string, Picked> };
const NONE: Selection = { mode: "some", items: new Map() };

const itemOf = (key: string) => {
  const [item_type, ...rest] = key.split("|");
  return { item_type, item_key: rest.join("|") };
};
const effectiveCode = (row: OrgUnitRow) => row.target_id ?? row.id;
/** Quando o lápis aparece: a regra única `canEditOrgUnit` (spec 2026-10-26, D24). */
const canEditRow = (row: OrgUnitRow) =>
  canEditOrgUnit({
    origin: row.origin,
    levelCode: row.level_code,
    migrationStatus: row.migration_status,
  });
/**
 * Voltar a pendente: só a manual (a decisão da extraída é do Diagnóstico) e só enquanto o CBC
 * não a pegou. Em cadastro (`running`, `awaiting_human_decision`) o api recusa com 409; a
 * cadastrada (`completed`) mostra o duplo check no lugar do botão.
 */
const canReturnRow = (row: OrgUnitRow) =>
  row.origin === "manual" &&
  row.migration_status !== "running" &&
  row.migration_status !== "awaiting_human_decision";
/** O ID corrigido de uma unidade extraída, com o extraído riscado ao lado (D30). */
const codeOf = (row: OrgUnitRow) => {
  const code = effectiveCode(row);
  return row.overridden_fields.includes("id") ? (
    <CorrectedValue
      extracted={row.original_values.id ?? null}
      current={code}
      emptyLabel="—"
    />
  ) : (
    code
  );
};
const scopeUnitOfRow = (row: OrgUnitRow): ScopeUnit => ({
  key: row.key,
  id: row.id,
  targetId: row.target_id ?? null,
  name: row.name ?? null,
  levelName: row.level_name,
});
const scopeUnitOfItem = (item: ChangedItem): ScopeUnit => ({
  key: item.key,
  id: item.code,
  targetId: null,
  name: item.name ?? null,
  levelName: item.label ?? "",
});

interface Filters {
  q: string;
  unitType: string;
  country: string;
  source: SourceFilter;
}
const NO_FILTERS: Filters = { q: "", unitType: "", country: "", source: "all" };
type ListFilters = Pick<
  OrgUnitQuery,
  "q" | "unitTypes" | "countries" | "origin"
>;

/**
 * Aba da estrutura organizacional no Gerenciamento. A lista vem do servidor em
 * páginas (`/management/org-units`): raízes de 30 em 30, filhos ao abrir a
 * seta, decididos em páginas de 15. O mapa carrega páginas da hierarquia física.
 */
export function OrgManagementTab({
  projectId,
  artifactId,
  view,
  counts,
}: {
  projectId: string;
  artifactId: string;
  view: "list" | "map";
  counts: NodeCountsView;
}) {
  const { t } = useTranslation();
  const draft = useManagementDraft();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [q, commitSearch] = useDebouncedSearch(filters.q);
  // Os mesmos filtros valem para as três listas (pendentes, aprovados e fora do escopo).
  const listFilters = useMemo<ListFilters>(
    () => ({
      q,
      unitTypes: filters.unitType ? [filters.unitType] : [],
      countries: filters.country ? [filters.country] : [],
      origin:
        filters.source === "all"
          ? undefined
          : filters.source === "erp"
            ? "sap"
            : "manual",
    }),
    [q, filters.unitType, filters.country, filters.source],
  );
  const query = useMemo<OrgUnitQuery>(
    () => ({ verdict: "pending", ...listFilters }),
    [listFilters],
  );
  // Raízes pendentes da lista (a consulta sobe para cá porque a barra fica na tela mesmo sem pendentes).
  const roots = useOrgUnitsScroll(
    projectId,
    artifactId,
    query,
    view === "list",
  );
  // Tipos e países da barra: união dos três vereditos, sem filtro, que não encolhe ao filtrar.
  const facets = useOrgFacets(projectId, artifactId);
  const manualPending = useOrgUnitsPage(
    projectId,
    artifactId,
    { ...query, origin: "manual" },
    0,
    query.origin !== "sap",
  );
  const draftFingerprint = [...(draft?.verdicts.org_structure ?? [])].sort(
    ([left], [right]) => left.localeCompare(right),
  );
  // A listagem conta o estado salvo. A prévia repete o rascunho antes de resolver
  // "todos", inclusive filtros e unidades fora da primeira página. Manter os
  // pendentes como pendentes só conta, sem tentar aprovar os pais do Diagnóstico.
  const eligiblePreview = useQuery({
    queryKey: [
      ...stageKeys.all(projectId),
      "management",
      "eligible-manual",
      artifactId,
      query,
      draft?.previewRevision,
    ],
    queryFn: () =>
      draft!.previewWith({
        node: "org_structure",
        verdict: "pending",
        items: [],
        all_pending: {
          q: query.q || undefined,
          unit_types: query.unitTypes,
          countries: query.countries,
          origin: "manual",
        },
      }),
    enabled:
      !!draft?.previewCurrent &&
      draftFingerprint.length > 0 &&
      query.origin !== "sap" &&
      !!manualPending.data &&
      !manualPending.isPlaceholderData,
  });
  const manualCount =
    query.origin === "sap" ||
    manualPending.isPlaceholderData ||
    (draft && !draft.previewCurrent)
      ? 0
      : draftFingerprint.length > 0
        ? (eligiblePreview.data?.last_requested_count ?? 0)
        : (manualPending.data?.matching_total ?? 0);
  const filtering = !!(
    q.trim() ||
    filters.unitType ||
    filters.country ||
    filters.source !== "all"
  );
  const [selection, setSelection] = useState<Selection>(NONE);
  const [mapSelected, setMapSelected] = useState<Map<string, OrgTreeNode>>(
    new Map(),
  );
  const [tool, setTool] = useState<ToolRequest | null>(null);
  const [confirm, setConfirm] = useState<{
    operation: DraftOperation;
    node: ScopeUnit;
    affected: ScopeUnit[];
    count: number;
    alertParents: boolean;
  } | null>(null);
  // Aprovou Distribution Channel ou Division sem nenhuma Sales Organization ligada aprovada:
  // aviso, sem aprovar nada sozinho; a pessoa aprova a Sales Organization à mão.
  const [parentAlert, setParentAlert] = useState(false);
  const [announcement, setAnnouncement] = useLiveAnnouncement();
  const [error, setError] = useState<string | null>(null);
  const returnedDetails = useDraftItems("org_structure", "pending");
  const returned = returnedDetails.items;

  useEffect(() => {
    setSelection(NONE);
  }, [query]);

  const message = useCallback(
    (verdict: ScopeVerdict, count: number) =>
      verdict === "approved"
        ? t("{{count}} items approved", { count })
        : verdict === "out_of_scope"
          ? t("{{count}} items taken out of scope", { count })
          : t("{{count}} items returned to pending", { count }),
    [t],
  );

  /** Pergunta ao servidor o efeito (cascata); com efeito colateral, confirma antes. */
  const request = useCallback(
    async (
      operation: DraftOperation,
      node: ScopeUnit,
      count: number,
      alertParents = false,
    ) => {
      if (!draft) return;
      setError(null);
      let effectiveCount = count;
      try {
        const preview = await draft.previewWith(operation);
        effectiveCount = operation.all_pending
          ? (preview.last_requested_count ?? 0)
          : count;
        if (operation.all_pending && effectiveCount === 0) {
          setAnnouncement(t("No units in this list can be decided here."));
          setError(t("No units in this list can be decided here."));
          return;
        }
        if (preview.last_affected.length > 0) {
          setConfirm({
            operation,
            node,
            count: effectiveCount,
            affected: preview.last_affected.map(scopeUnitOfItem),
            alertParents,
          });
          return;
        }
      } catch (error) {
        setError(
          configurationErrorMessage(bulkVerdictError(error)?.code, t) ??
            errorDetailOr(error, t("Could not prepare this decision.")),
        );
        return;
      }
      draft.stage(operation);
      setAnnouncement(message(operation.verdict, effectiveCount));
      if (alertParents && operation.verdict === "approved")
        setParentAlert(true);
    },
    [draft, t, setAnnouncement, message],
  );

  const decideRow = (row: OrgUnitRow, verdict: ScopeVerdict) => {
    if (row.origin !== "manual") return;
    void request(
      { node: "org_structure", verdict, items: [itemOf(row.key)] },
      scopeUnitOfRow(row),
      1,
      !!row.needs_parent_approval,
    );
  };
  const decideItem = (item: ChangedItem, verdict: ScopeVerdict) =>
    void request(
      { node: "org_structure", verdict, items: [itemOf(item.key)] },
      scopeUnitOfItem(item),
      1,
    );
  const decideNode = (node: OrgTreeNode, verdict: ScopeVerdict) => {
    if (node.origin !== "manual") return;
    void request(
      {
        node: "org_structure",
        verdict,
        items: [{ item_type: node.levelCode, item_key: node.itemKey }],
      },
      {
        key: node.key,
        id: node.id,
        targetId: node.targetId,
        name: node.name,
        levelName: node.levelName,
      },
      1,
    );
  };

  const selectedCount =
    view === "map"
      ? mapSelected.size
      : selection.mode === "all"
        ? manualCount
        : selection.items.size;
  const decideSelected = (verdict: Verdict) => {
    if (view === "map") {
      const nodes = [...mapSelected.values()].filter(
        (node) => node.origin === "manual",
      );
      if (nodes.length === 0) return;
      void request(
        {
          node: "org_structure",
          verdict,
          items: nodes.map((n) => ({
            item_type: n.levelCode,
            item_key: n.itemKey,
          })),
        },
        {
          key: nodes[0].key,
          id: nodes[0].id,
          targetId: nodes[0].targetId,
          name: nodes[0].name,
          levelName: nodes[0].levelName,
        },
        nodes.length,
      );
      setMapSelected(new Map());
      return;
    }
    if (selection.mode === "all") {
      void request(
        {
          node: "org_structure",
          verdict,
          items: [],
          all_pending: {
            q: query.q || undefined,
            unit_types: query.unitTypes,
            countries: query.countries,
            origin: "manual",
          },
        },
        { key: "all", id: t("All"), targetId: null, name: null, levelName: "" },
        manualCount,
      );
    } else {
      const picked = [...selection.items.values()].filter(
        (entry) => entry.row?.origin === "manual",
      );
      if (picked.length === 0) return;
      const first = picked[0];
      void request(
        {
          node: "org_structure",
          verdict,
          items: picked.map((p) => ({
            ...itemOf(p.key),
            with_descendants: p.withDescendants,
          })),
        },
        first.row
          ? scopeUnitOfRow(first.row)
          : {
              key: first.key,
              id: itemOf(first.key).item_key,
              targetId: null,
              name: null,
              levelName: "",
            },
        picked.length,
        picked.some((p) => p.row?.needs_parent_approval),
      );
    }
    setSelection(NONE);
  };

  const mapSelection: OrgSelection = useMemo(
    () => ({
      selectedKeys: new Set(mapSelected.keys()),
      onToggle: (nodes, checked) =>
        setMapSelected((current) => {
          const next = new Map(current);
          for (const node of nodes) {
            if (checked && node.origin === "manual") next.set(node.key, node);
            else next.delete(node.key);
          }
          return next;
        }),
    }),
    [mapSelected],
  );

  const onManagementChanged = () => {
    void invalidateConfigQueries(queryClient, stageKeys.all(projectId));
    void invalidateConfigQueries(queryClient, ["migration-items", projectId]);
  };

  const pending =
    view === "map" ? (
      <BoundedOrgMap
        projectId={projectId}
        artifactId={artifactId}
        query={query}
        selection={mapSelection}
        onVerdict={decideNode}
        onReturn={(node) => decideNode(node, "pending")}
      />
    ) : (
      <OrgPendingList
        projectId={projectId}
        artifactId={artifactId}
        roots={roots}
        filtering={filtering}
        returned={returned}
        returnedFooter={
          returnedDetails.hasMore ||
          returnedDetails.loading ||
          returnedDetails.error ? (
            <DraftDetailsMore details={returnedDetails} />
          ) : undefined
        }
        selection={selection}
        onSelectionChange={setSelection}
        allCount={counts.pending}
        selectableCount={manualCount}
        countFailed={eligiblePreview.isError}
        onDecide={decideRow}
        onDecideItem={decideItem}
        onEdit={(key) => setTool({ kind: "edit", key })}
        onDetails={(key) => setTool({ kind: "details", key })}
      />
    );

  return (
    <div>
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement.message && (
          <span key={announcement.sequence}>{announcement.message}</span>
        )}
      </p>
      {error && <p role="alert">{error}</p>}
      <ScopeVerdictDialog
        open={!!confirm}
        node={confirm?.node ?? null}
        selectedCount={confirm?.count}
        affected={confirm?.affected ?? []}
        verdict={confirm?.operation.verdict ?? null}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) {
            draft?.stage(confirm.operation);
            setAnnouncement(message(confirm.operation.verdict, confirm.count));
            if (
              confirm.alertParents &&
              confirm.operation.verdict === "approved"
            )
              setParentAlert(true);
          }
          setConfirm(null);
        }}
      />
      <AlertDialog
        open={parentAlert}
        onOpenChange={(open) => !open && setParentAlert(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("A Sales Organization is needed")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "A Distribution Channel or a Division is registered with a Sales Organization. None of the Sales Organizations linked to it is approved. Approve at least one of them yourself; nothing was approved automatically.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setParentAlert(false)}>
              {t("Close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {tool && (
        <BoundedOrgTool
          projectId={projectId}
          artifactId={artifactId}
          tool={tool}
          onClose={() => setTool(null)}
          onVerdict={decideNode}
          onChanged={onManagementChanged}
        />
      )}

      {
        <div className="mb-4">
          <ManagementToolbar
            search={filters.q}
            onSearch={(value) => setFilters({ ...filters, q: value })}
            onSearchEnter={commitSearch}
            searchLabel={t("Search code or name…")}
            filters={
              <>
                <select
                  aria-label={t("All unit types")}
                  value={filters.unitType}
                  onChange={(event) =>
                    setFilters({ ...filters, unitType: event.target.value })
                  }
                  className={FILTER_SELECT_CLASS}
                >
                  <option value="">{t("All unit types")}</option>
                  {facets.unitTypes.map((facet) => (
                    <option key={facet.value} value={facet.value}>
                      {t(orgUnitTypeLabelKey(undefined, facet.label), {
                        defaultValue: facet.label,
                      })}
                    </option>
                  ))}
                </select>
                {facets.countries.length > 0 && (
                  <select
                    aria-label={t("All countries/regions")}
                    value={filters.country}
                    onChange={(event) =>
                      setFilters({ ...filters, country: event.target.value })
                    }
                    className={FILTER_SELECT_CLASS}
                  >
                    <option value="">{t("All countries/regions")}</option>
                    {facets.countries.map((facet) => (
                      <option key={facet.value} value={facet.value}>
                        {facet.label}
                      </option>
                    ))}
                  </select>
                )}
                <SourceSegment
                  value={filters.source}
                  onChange={(source) => setFilters({ ...filters, source })}
                />
              </>
            }
            action={
              <Button
                className="ml-auto h-9 gap-1.5 bg-[var(--exed-navy)] px-3 text-[13px] font-medium text-white hover:bg-[var(--exed-navy)]/90"
                onClick={() => setTool({ kind: "create" })}
              >
                <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                {t("Create organizational unit")}
              </Button>
            }
          />
        </div>
      }
      <TransferLayout
        pendingOnly={view === "map"}
        decidedOnly={
          view === "list" && counts.pending === 0 && returned.length === 0
        }
        pendingTitle={t("Pending")}
        pendingCount={counts.pending}
        pendingActions={
          <BulkActions
            count={selectedCount}
            onApprove={() => decideSelected("approved")}
            onOutOfScope={() => decideSelected("out_of_scope")}
            onClear={() => {
              setSelection(NONE);
              setMapSelected(new Map());
            }}
          />
        }
        pending={pending}
        approvedCount={counts.approved}
        approved={
          <OrgDecidedPanel
            projectId={projectId}
            artifactId={artifactId}
            verdict="approved"
            filters={listFilters}
            onReturn={decideRow}
            onReturnItem={decideItem}
            onEdit={(key) => setTool({ kind: "edit", key })}
            onDetails={(key) => setTool({ kind: "details", key })}
          />
        }
        outOfScopeCount={counts.out_of_scope}
        outOfScope={
          <OrgDecidedPanel
            projectId={projectId}
            artifactId={artifactId}
            verdict="out_of_scope"
            filters={listFilters}
            onReturn={decideRow}
            onReturnItem={decideItem}
            onEdit={(key) => setTool({ kind: "edit", key })}
            onDetails={(key) => setTool({ kind: "details", key })}
          />
        }
      />
    </div>
  );
}

interface RowContext {
  projectId: string;
  artifactId: string;
  expanded: ReadonlySet<string>;
  onToggleExpand: (key: string) => void;
  isChecked: (key: string, inherited: boolean) => boolean;
  onCheck: (
    row: OrgUnitRow,
    checked: boolean,
    inherited: boolean,
    siblings: OrgUnitRow[],
  ) => void;
  onDecide: (row: OrgUnitRow, verdict: ScopeVerdict) => void;
  onEdit: (key: string) => void;
  onDetails: (key: string) => void;
  draftVerdicts: ReadonlyMap<string, ScopeVerdict>;
  filtering: boolean;
}

function OrgPendingList({
  projectId,
  artifactId,
  roots,
  filtering,
  returned,
  returnedFooter,
  selection,
  onSelectionChange,
  allCount,
  selectableCount,
  countFailed,
  onDecide,
  onDecideItem,
  onEdit,
  onDetails,
}: {
  projectId: string;
  artifactId: string;
  roots: ReturnType<typeof useOrgUnitsScroll>;
  filtering: boolean;
  returned: ChangedItem[];
  returnedFooter?: ReactNode;
  selection: Selection;
  onSelectionChange: (update: (current: Selection) => Selection) => void;
  allCount: number;
  selectableCount: number;
  countFailed: boolean;
  onDecide: (row: OrgUnitRow, verdict: ScopeVerdict) => void;
  onDecideItem: (item: ChangedItem, verdict: ScopeVerdict) => void;
  onEdit: (key: string) => void;
  onDetails: (key: string) => void;
}) {
  const { t } = useTranslation();
  const draftVerdicts = useDraftVerdicts("org_structure");
  const loaded = useMemo(
    () => roots.data?.pages.flatMap((page) => page.items) ?? [],
    [roots.data],
  );
  const rows = loaded.filter(
    (row) => (draftVerdicts.get(row.key) ?? "pending") === "pending",
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const visibleRef = useRef<OrgUnitRow[]>([]);
  visibleRef.current = rows;

  const toggleExpand = useCallback(
    (key: string) =>
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  const ctx: RowContext = {
    projectId,
    artifactId,
    expanded,
    onToggleExpand: toggleExpand,
    isChecked: (key, inherited) =>
      selection.mode === "all" || inherited || selection.items.has(key),
    onCheck: (row, checked, inherited, siblings) => {
      if (row.origin !== "manual") return;
      onSelectionChange((current) => {
        const base = new Map<string, Picked>(
          current.mode === "all"
            ? visibleRef.current
                .filter((r) => r.origin === "manual")
                .map((r) => [
                  r.key,
                  { key: r.key, row: r, withDescendants: r.child_count > 0 },
                ])
            : current.items,
        );
        if (inherited && !checked) {
          // Filho de um pai marcado "com os filhos": o pai deixa de levar todos e
          // os irmãos visíveis entram um a um.
          for (const [key, picked] of base)
            if (picked.withDescendants && row.parent_key === key)
              base.set(key, { ...picked, withDescendants: false });
          for (const sibling of siblings)
            if (sibling.key !== row.key && sibling.origin === "manual")
              base.set(sibling.key, {
                key: sibling.key,
                row: sibling,
                withDescendants: false,
              });
        } else if (checked) {
          base.set(row.key, {
            key: row.key,
            row,
            withDescendants: row.child_count > 0,
          });
        } else {
          base.delete(row.key);
        }
        return { mode: "some", items: base };
      });
      if (checked && row.child_count > 0)
        setExpanded((current) =>
          current.has(row.key) ? current : new Set([...current, row.key]),
        );
    },
    onDecide,
    onEdit,
    onDetails,
    draftVerdicts,
    filtering,
  };

  const selectableRows = rows.filter((row) => row.origin === "manual");
  const selectedHere =
    selection.mode === "all"
      ? selectableRows.length
      : selectableRows.filter((row) => selection.items.has(row.key)).length;
  const allState =
    selection.mode === "all"
      ? true
      : selectedHere > 0 ||
          (selection.mode === "some" && selection.items.size > 0)
        ? "indeterminate"
        : false;

  return (
    <div>
      <SelectAllBar
        checked={allState}
        shown={selectableRows.length}
        disabled={selectableCount === 0}
        onChange={(checked) =>
          onSelectionChange(() => (checked ? { mode: "all" } : NONE))
        }
      />
      {countFailed && (
        <p className="m-0 border-b border-[var(--exed-card-border)] px-5 py-1.5 text-xs text-[var(--exed-ink-500)]">
          {t("Could not count the units you can decide here.")}
        </p>
      )}
      {(returned.length > 0 || returnedFooter) && (
        <section
          aria-label={t("Not saved")}
          className="border-b border-[var(--exed-card-border)] bg-[rgba(255,200,61,0.06)]"
        >
          <h4 className="m-0 px-5 py-2 text-xs font-semibold text-[#7a5600]">
            {t("Not saved")} · {returned.length.toLocaleString("en-US")}
          </h4>
          <ul>
            {returned.map((item) => (
              <li
                key={item.key}
                data-org-key={item.key}
                className="flex items-center gap-2.5 border-t border-[#f3ead2] px-5 py-2"
              >
                <span className="min-w-[44px] rounded bg-[#f1f3f6] px-1.5 py-0.5 text-center font-mono text-xs font-medium text-[var(--exed-ink-900)]">
                  {item.code}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--exed-ink-900)]">
                  {item.name ?? <MissingName />}
                </span>
                <ApproveButton
                  data-pending-action=""
                  label={t("Approve {{code}}", { code: item.code })}
                  onClick={() => onDecideItem(item, "approved")}
                />
                <OutOfScopeButton
                  label={t("Take {{code}} out of scope", { code: item.code })}
                  onClick={() => onDecideItem(item, "out_of_scope")}
                />
              </li>
            ))}
          </ul>
          {returnedFooter}
        </section>
      )}
      {roots.isError ? (
        <div role="alert" className="p-5">
          <p>{t("Could not load the hierarchy")}</p>
          <Button onClick={() => void roots.refetch()}>{t("Retry")}</Button>
        </div>
      ) : roots.isPending ? (
        <div className="p-5">
          <StageLoading variant="list" label={t("Loading the hierarchy…")} />
        </div>
      ) : rows.length === 0 && !roots.hasNextPage ? (
        <EmptyPending />
      ) : (
        <ul>
          {rows.map((row) => (
            <OrgPendingRow
              key={row.key}
              row={row}
              depth={0}
              inherited={false}
              siblings={rows}
              ctx={ctx}
            />
          ))}
        </ul>
      )}
      <LoadMore
        hasMore={!!roots.hasNextPage}
        loading={roots.isFetchingNextPage}
        onLoadMore={() => void roots.fetchNextPage()}
      />
      <span className="sr-only">
        {t("{{count}} items", { count: allCount })}
      </span>
    </div>
  );
}

function OrgPendingRow({
  row,
  depth,
  inherited,
  siblings,
  ctx,
}: {
  row: OrgUnitRow;
  depth: number;
  inherited: boolean;
  siblings: OrgUnitRow[];
  ctx: RowContext;
}) {
  const { t } = useTranslation();
  const code = effectiveCode(row);
  const open = ctx.expanded.has(row.key);
  const checked = ctx.isChecked(row.key, inherited);
  const originalName = row.overridden_fields.includes("name")
    ? row.original_values.name
    : undefined;
  const name = row.name ?? <MissingName />;
  const fromDiagnostic = row.origin !== "manual";
  // Mesma chave de `OrgChildren`: o react-query divide o cache, sem segunda requisição; aqui só para a seta saber que carrega.
  const kids = useOrgUnitsScroll(
    ctx.projectId,
    ctx.artifactId,
    { verdict: "pending", parent: row.key },
    open && !ctx.filtering,
  );
  const loadingChildren = open && !ctx.filtering && kids.isPending;
  return (
    <>
      <DecisionRow
        item={{
          code: codeOf(row),
          name:
            originalName !== undefined ? (
              <CorrectedValue
                extracted={originalName}
                current={name}
                emptyLabel={t("name not extracted")}
              />
            ) : (
              name
            ),
          type: t(
            orgUnitTypeLabelKey(row.subtype ?? undefined, row.level_name),
            { defaultValue: row.level_name },
          ),
          source: row.origin === "manual" ? "manual" : undefined,
        }}
        depth={depth}
        checked={fromDiagnostic ? false : checked}
        onCheck={
          fromDiagnostic
            ? undefined
            : (value) => ctx.onCheck(row, value, inherited, siblings)
        }
        checkLabel={t("Select {{name}}", { name: code })}
        rowProps={{ "data-testid": `org-row-${row.key}` }}
        expander={
          !ctx.filtering && row.child_count > 0
            ? {
                expanded: open,
                onToggle: () => ctx.onToggleExpand(row.key),
                label: t(open ? "Collapse {{id}}" : "Expand {{id}}", {
                  id: code,
                }),
                loading: loadingChildren,
              }
            : null
        }
        actions={
          row.migration_status === "completed" ? (
            <div className="flex items-center gap-1">
              {fromDiagnostic && <DiagnosticBadge />}
              <OrgUnitStatusBadge status="completed" />
            </div>
          ) : fromDiagnostic ? (
            <div
              className="flex shrink-0 items-center gap-1"
              data-org-key={row.key}
            >
              <DiagnosticBadge />
              {/* Editar o ID não decide escopo: aprovar e tirar do escopo seguem no Diagnóstico (D27). */}
              {canEditRow(row) && (
                <GhostIconButton
                  label={t("Edit {{id}}", { id: code })}
                  title={t("Edit")}
                  icon={<Pencil />}
                  onClick={() => ctx.onEdit(row.key)}
                />
              )}
              <GhostIconButton
                label={t("View details for {{id}}", { id: code })}
                title={t("View details")}
                icon={<PanelRightOpen />}
                onClick={() => ctx.onDetails(row.key)}
              />
            </div>
          ) : (
            <PendingActions
              approveLabel={t("Approve {{code}}", { code })}
              outLabel={t("Take {{code}} out of scope", { code })}
              editLabel={t("Edit {{id}}", { id: code })}
              detailsLabel={t("View details for {{id}}", { id: code })}
              containerProps={{ "data-org-key": row.key }}
              onApprove={() => ctx.onDecide(row, "approved")}
              onOut={() => ctx.onDecide(row, "out_of_scope")}
              onEdit={() => ctx.onEdit(row.key)}
              onDetails={() => ctx.onDetails(row.key)}
            />
          )
        }
      />
      {open && (
        <OrgChildren
          parentKey={row.key}
          depth={depth + 1}
          inherited={checked && !inherited ? true : inherited}
          ctx={ctx}
        />
      )}
    </>
  );
}

function OrgChildren({
  parentKey,
  depth,
  inherited,
  ctx,
}: {
  parentKey: string;
  depth: number;
  inherited: boolean;
  ctx: RowContext;
}) {
  const children = useOrgUnitsScroll(ctx.projectId, ctx.artifactId, {
    verdict: "pending",
    parent: parentKey,
  });
  const rows = (
    children.data?.pages.flatMap((page) => page.items) ?? []
  ).filter(
    (row) => (ctx.draftVerdicts.get(row.key) ?? "pending") === "pending",
  );
  // O DecisionRow recua 20 + 30 por nível; checkbox (16) e espaço (10) levam a seta dos filhos a 46.
  const base = 46;
  // Em busca/filtro a lista vem achatada: sem hierarquia, não há o que esperar.
  if (children.isPending)
    return ctx.filtering ? null : <ChildrenLoading depth={depth} base={base} />;
  // Sem dados, o erro ocupa o lugar das linhas; com dados (releitura ou página seguinte que falhou), as linhas ficam e o erro vem depois.
  if (children.isError && !children.data)
    return (
      <ChildrenError
        depth={depth}
        base={base}
        onRetry={() => void children.refetch()}
      />
    );
  return (
    <>
      {rows.map((row) => (
        <OrgPendingRow
          key={row.key}
          row={row}
          depth={depth}
          inherited={inherited}
          siblings={rows}
          ctx={ctx}
        />
      ))}
      {children.isError && (
        <ChildrenError
          depth={depth}
          base={base}
          onRetry={() => void children.refetch()}
        />
      )}
      {children.hasNextPage && (
        <li style={{ paddingLeft: `${20 + depth * 30}px` }}>
          <LoadMore
            hasMore
            loading={children.isFetchingNextPage}
            onLoadMore={() => void children.fetchNextPage()}
          />
        </li>
      )}
    </>
  );
}

function OrgDecidedPanel({
  projectId,
  artifactId,
  verdict,
  filters,
  onReturn,
  onReturnItem,
  onEdit,
  onDetails,
}: {
  projectId: string;
  artifactId: string;
  verdict: Verdict;
  filters: ListFilters;
  onReturn: (row: OrgUnitRow, verdict: ScopeVerdict) => void;
  onReturnItem: (item: ChangedItem, verdict: ScopeVerdict) => void;
  onEdit: (key: string) => void;
  onDetails: (key: string) => void;
}) {
  const { t } = useTranslation();
  const draftDetails = useDraftItems("org_structure", verdict);
  const draftVerdicts = useDraftVerdicts("org_structure");
  const { q = "" } = filters;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const result = useOrgUnitsScroll(
    projectId,
    artifactId,
    { verdict, ...filters },
    true,
    PANEL_PAGE_SIZE,
  );
  const rows = (result.data?.pages.flatMap((page) => page.items) ?? []).filter(
    (row) => (draftVerdicts.get(row.key) ?? verdict) === verdict,
  );
  const unsaved = draftDetails.items.filter(
    (item) =>
      !q ||
      `${item.code} ${item.name ?? ""}`
        .toLocaleLowerCase()
        .includes(q.toLocaleLowerCase()),
  );
  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  if (result.isError)
    return (
      <div role="alert" className="p-4">
        <p>{t("Could not load the hierarchy")}</p>
        <Button onClick={() => void result.refetch()}>{t("Retry")}</Button>
      </div>
    );
  if (result.isPending) return <StageLoading variant="panel" />;
  // Com qualquer filtro o servidor devolve as unidades achatadas, sem hierarquia.
  const searching =
    q.trim().length > 0 ||
    !!filters.unitTypes?.length ||
    !!filters.countries?.length ||
    !!filters.origin;
  const renderRow = (row: OrgUnitRow, depth: number): JSX.Element => {
    const code = effectiveCode(row);
    const open = expanded.has(row.key);
    return (
      <DecidedRowWithChildren
        key={row.key}
        row={row}
        depth={depth}
        open={open}
        searching={searching}
        onToggle={() => toggle(row.key)}
        renderRow={renderRow}
        projectId={projectId}
        artifactId={artifactId}
        verdict={verdict}
        draftVerdicts={draftVerdicts}
        returnLabel={t("Return {{code}} to pending", { code })}
        onReturn={
          canReturnRow(row) ? () => onReturn(row, "pending") : undefined
        }
        onEdit={canEditRow(row) ? () => onEdit(row.key) : undefined}
        onDetails={() => onDetails(row.key)}
      />
    );
  };
  return (
    <DecidedPanelBody
      bare
      filtering={searching}
      hasItems={(result.data?.pages[0]?.total ?? 0) > 0 || unsaved.length > 0}
      hasMatches={rows.length > 0 || unsaved.length > 0}
      footer={
        <LoadMore
          hasMore={!!result.hasNextPage}
          loading={result.isFetchingNextPage}
          onLoadMore={() => void result.fetchNextPage()}
        />
      }
    >
      {(unsaved.length > 0 ||
        draftDetails.hasMore ||
        draftDetails.loading ||
        draftDetails.error) && (
        <section
          aria-label={t("Not saved")}
          className="bg-[rgba(255,200,61,0.06)]"
        >
          <h4 className="m-0 px-3 py-2 text-xs font-semibold text-[#7a5600]">
            {t("Not saved")} · {unsaved.length.toLocaleString("en-US")}
          </h4>
          <ul>
            {unsaved.map((item) => (
              <DecidedRow
                key={item.key}
                code={item.code}
                name={item.name ?? <MissingName />}
                path={item.parent_id}
                rowProps={{ "data-testid": `unsaved-${item.key}` }}
                returnLabel={t("Return {{code}} to pending", {
                  code: item.code,
                })}
                onReturn={() => onReturnItem(item, "pending")}
                detailsLabel={t("View details for {{id}}", { id: item.code })}
                onDetails={() => onDetails(item.key)}
              />
            ))}
          </ul>
          <DraftDetailsMore details={draftDetails} />
        </section>
      )}
      <ul>{rows.map((row) => renderRow(row, 0))}</ul>
    </DecidedPanelBody>
  );
}

function DecidedRowWithChildren({
  row,
  depth,
  open,
  searching,
  onToggle,
  renderRow,
  projectId,
  artifactId,
  verdict,
  draftVerdicts,
  returnLabel,
  onReturn,
  onEdit,
  onDetails,
}: {
  row: OrgUnitRow;
  depth: number;
  open: boolean;
  searching: boolean;
  onToggle: () => void;
  renderRow: (row: OrgUnitRow, depth: number) => JSX.Element;
  projectId: string;
  artifactId: string;
  verdict: Verdict;
  draftVerdicts: ReadonlyMap<string, ScopeVerdict>;
  returnLabel: string;
  onReturn?: () => void;
  onEdit?: () => void;
  onDetails: () => void;
}) {
  const { t } = useTranslation();
  const code = effectiveCode(row);
  const children = useOrgUnitsScroll(
    projectId,
    artifactId,
    { verdict, parent: row.key },
    open && !searching,
  );
  const kids = (
    children.data?.pages.flatMap((page) => page.items) ?? []
  ).filter((child) => (draftVerdicts.get(child.key) ?? verdict) === verdict);
  // Com a consulta desligada o status também é "pending": só conta como carregando quando a unidade está aberta.
  const showChildren = open && !searching;
  const loadingChildren = showChildren && children.isPending;
  return (
    <>
      <DecidedRow
        code={codeOf(row)}
        name={row.name ?? <MissingName />}
        path={searching ? row.parent_id : null}
        depth={depth}
        rowProps={{ "data-testid": `decided-${row.key}` }}
        expander={
          !searching && row.child_count > 0
            ? {
                expanded: open,
                onToggle,
                label: t(open ? "Collapse {{id}}" : "Expand {{id}}", {
                  id: code,
                }),
                loading: loadingChildren,
              }
            : null
        }
        doneLabel={
          row.migration_status === "completed"
            ? t("Registered in CBC")
            : undefined
        }
        badge={row.origin !== "manual" ? <DiagnosticBadge /> : undefined}
        returnLabel={returnLabel}
        onReturn={onReturn}
        editLabel={t("Edit {{id}}", { id: code })}
        onEdit={onEdit}
        detailsLabel={t("View details for {{id}}", { id: code })}
        onDetails={onDetails}
      />
      {loadingChildren && <ChildrenLoading depth={depth + 1} />}
      {showChildren && children.isError && (
        <ChildrenError
          depth={depth + 1}
          onRetry={() => void children.refetch()}
        />
      )}
      {showChildren && kids.map((child) => renderRow(child, depth + 1))}
      {showChildren && children.hasNextPage && (
        <li className={cn("px-3")}>
          <LoadMore
            hasMore
            loading={children.isFetchingNextPage}
            onLoadMore={() => void children.fetchNextPage()}
          />
        </li>
      )}
    </>
  );
}
